import { TableFilterSubmitProps } from "@material-dev/lrw-insights-portal-ui-datatable";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk, RootState } from "@store";
import {
  CompositeMergeProjectCommand,
  CompositeMergeResponseDto,
  CompositeProjectMetadataDto,
  DataCollectionVariableModel,
  GetDataDictionariesPageQuery,
  GetVariablesFilterOptionsQuery,
  IProjectsDataClient,
  PageContextOfProjectDataFilter,
  ProjectDataFilter,
  ProjectsDataClient,  
  VariableDto,
} from "app/ApiClient";
import { VariableDtoKey } from "components/ProjectData/hooks/useProjectDataColumns";
import { SHOW_ERROR_LOAD_DELAY } from "const/config";
import { LoadingState, VariablesBatteryGroupSelectableType, VariablesBatteryGroupsType } from "types";

import { IState } from "..";
import ProjectsDataState, { originProjectsDataState } from "./projectsDataState";
import { updateCurrentProjectName } from '../project/projectSlice';
import { updateProjectName } from '../projects/projectsSlice';
import { getCompositeMergeStatus, addMergeTriggerHistory, setLastMergeRequest } from '../compositeProject/compositeProjectSlice'
import { isDefaultBatteryName } from "components/ProjectData/utils";

const COMPOSITE_PROJECT_MERGE_IN_PROGRESS_MSG =
  "Merge already in progress for the selected dataset. Please wait for it to complete.";

const ALREADY_IN_PROGRESS_TEXT = "merge process already in progress";

const projectsDataClient: IProjectsDataClient = new ProjectsDataClient(process.env.REACT_APP_API_URL);

export const getRespondentsCount = createAsyncThunk(
  "projectsData/getRespondentsCount",
  async (
    {
      modificationId,
      projectId,
      versionId,
    }: { modificationId: Nullable<number>; projectId: number; versionId: string },
    { rejectWithValue },
  ) => {
    try {
      return await projectsDataClient.getRespondentsCount(projectId, versionId, modificationId);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const rowDisabledCriteria = (row:VariablesBatteryGroupSelectableType) => !(row?.battery===undefined) && isDefaultBatteryName(row.battery);

export const projectsSlice = createSlice({
  name: "projectsData",
  initialState: originProjectsDataState,
  extraReducers: (builder) => {
    builder.addCase(getRespondentsCount.pending, (state) => {
      state.respondentsCount = 0;
      state.respondentsCountLoading = true;
      state.respondentsErrorMessage = undefined;
    });
    builder.addCase(getRespondentsCount.fulfilled, (state, { payload }) => {
      state.respondentsCount = payload;
      state.respondentsCountLoading = false;

    });
    builder.addCase(getRespondentsCount.rejected, (state, { error }) => {
      state.respondentsErrorMessage = error.message;
      state.respondentsCountLoading = false;
    });
    builder.addCase(getTableFilterOptions.pending, (state) => {
      state.tableFiltersOptionsLoading = LoadingState.pending;
    });
    builder.addCase(getTableFilterOptions.fulfilled, (state, action) => {
      const { payload } = action;

      const key = payload?.key as VariableDtoKey;
      const options = payload?.options ?? [];

      const keyExists = state.tableFiltersOptions?.some((opt) => opt.key === key);

      if (keyExists) {
        state.tableFiltersOptions = state.tableFiltersOptions?.map((opt) =>
          opt.key === key ? { ...opt, ...payload } : opt,
        );
      } else {
        state.tableFiltersOptions = [...state.tableFiltersOptions, { key, options }];
      }

      state.tableFiltersOptionsLoading = LoadingState.succeed;
    });
    builder.addCase(getTableFilterOptions.rejected, (state) => {
      state.tableFiltersOptionsLoading = LoadingState.failed;
    });
    builder.addCase(loadProjectDictionaryData.rejected, (state, { error: { message } }) => {
      state.processing = false;
      state.finished = true;
      state.succeed = false;
      state.failed = true;
      state.error = message ?? "";
    });
    builder.addCase(getCompositeProjectMetadata.pending, (state) => {
      state.compositeProjectLoadingState = LoadingState.pending;
    });

    builder.addCase(getCompositeProjectMetadata.fulfilled, (state, action: PayloadAction<CompositeProjectMetadataDto>) => {
      state.compositeProjectMetadata = action.payload;
      state.compositeProjectLoadingState = LoadingState.succeed;
    });
    builder.addCase(getCompositeProjectMetadata.rejected, (state) => {
      state.compositeProjectMetadata = null;
      state.compositeProjectLoadingState = LoadingState.failed;
    })
    builder.addCase(updateCompositeMetadata.pending, (state) => {
      state.compositeProjectLoadingState = LoadingState.pending;
    });
    builder.addCase(updateCompositeMetadata.fulfilled, (state, action: PayloadAction<CompositeProjectMetadataDto>) => {
      state.compositeProjectMetadata = action.payload;
      state.compositeProjectLoadingState = LoadingState.succeed;
    });
    builder.addCase(updateCompositeMetadata.rejected, (state) => {
      state.compositeProjectLoadingState = LoadingState.failed;
    });
    builder.addCase(compositeMergeProjects.pending, (state) => {
      state.compoisteProjectMergeStatus = LoadingState.pending;
      state.compositeProjectMergeResponse = null;
    });
    builder.addCase(compositeMergeProjects.fulfilled, (state, action: PayloadAction<CompositeMergeResponseDto>) => {
      state.compositeProjectMergeResponse = action.payload;
      const { error } = action.payload;
      if (error)
      {
        if (error.toLowerCase().includes(ALREADY_IN_PROGRESS_TEXT)) {
          state.compositeProjectMergeResponse.error = COMPOSITE_PROJECT_MERGE_IN_PROGRESS_MSG;
        }
        state.compoisteProjectMergeStatus = LoadingState.failed;
      }
      else
        state.compoisteProjectMergeStatus = LoadingState.succeed;

    });   

    builder.addCase(getProjectMatadata.pending, (state) => {
      state.metadataVariablesState = LoadingState.pending;
    });
    builder.addCase(getProjectMatadata.rejected, (state) => {
      state.metadataVariablesState = LoadingState.failed;
    });
    builder.addCase(getProjectMatadata.fulfilled, (state, action: PayloadAction<DataCollectionVariableModel[] | undefined>) => {
      state.metadataVariables = action.payload ?? [];
      state.metadataVariablesState = LoadingState.succeed;
    });

  },
  reducers: {
    setCurrentVersionId: (state, { payload }: PayloadAction<string | null>) => {
      state.currentVersionId = payload;
      state.compositeProjectMetadata = null;
    },
    setCurrentStackId: (state, { payload }: PayloadAction<number | null>) => {
      state.currentStackId = payload;
    },
    setCurrentModificationId: (state, { payload }: PayloadAction<number | null>) => {
      state.currentModificationId = payload;
    },
    updatePageDataVariable: (state, { payload }: PayloadAction<VariableDto>) => {
      state.variablesBatteryGroups =
        state.variablesBatteryGroups?.map((group) =>
          group.battery === payload.battery
            ? {
              ...group,
              variables: group.variables?.map((variable) =>
                variable.oldName === payload.oldName ? { ...variable, ...payload } : variable,
              ),
            }
            : group,
        ) ?? [];
    },
    resetUpateVariable: (state) => {
      state.currentVariable = null;
      state.updateVariableLoadingState = LoadingState.idle;
      state.updateVariableLoadingError = "";
    },
    resetProjectsData: () => originProjectsDataState,
    setCurrentVariable: (state, { payload }: { payload: VariableDto }) => {
      state.currentVariable = payload;
    },
    setDictionaryData: (state, { payload }: { payload: VariablesBatteryGroupsType[] }) => {
      state.processing = false;
      state.finished = true;
      state.succeed = true;
      state.failed = false;
      state.variablesBatteryGroups = payload;
    },
    setGenerateDictionaryDataDialogState: (state, { payload }: { payload: boolean }) => {
      state.isGenerateDictionaryDataDialogOpened = payload;
    },
    setTotal: (state, { payload }: { payload: number }) => {
      state.total = payload;
    },
    resetFilters: (state, { payload }: PayloadAction<boolean | undefined>) => {
      state.resetFilters = payload;
    },
    setPageContext: (state, { payload }: { payload: PageContextOfProjectDataFilter }) => {
      state.pageContext = payload;
    },
    projectsDataStart: (state) => {
      state.processing = true;
      state.finished = false;
      state.succeed = false;
      state.failed = false;
    },
    setVariablesUpsertLoadingState: (state, { payload }: { payload: LoadingState }) => {
      state.upsertVariablesLoadingState = payload;
    },
    setLoadingError: (state, { payload }: PayloadAction<boolean>) => {
      state.processing = false;
      state.showLoadingError = payload;
    },
    setHiddenColumns: (state, { payload }: PayloadAction<string[]>) => {
      state.hiddenColumns = payload;
    },
    resetCompositeProjectMatadata: (state) => {
      state.compositeProjectMetadata = null
    }
  },
});

export const {
  resetProjectsData,
  projectsDataStart,
  setDictionaryData,
  setGenerateDictionaryDataDialogState,
  resetFilters,
  setTotal,
  setCurrentVariable,
  resetUpateVariable,
  setVariablesUpsertLoadingState,
  setLoadingError,
  setPageContext,
  setCurrentVersionId,
  setCurrentStackId,
  setCurrentModificationId,
  updatePageDataVariable,
  setHiddenColumns,
  resetCompositeProjectMatadata,
} = projectsSlice.actions;

export const projectsDataSelector = (state: RootState): ProjectsDataState => state.projectsData;

export const loadProjectDictionaryData = createAsyncThunk(
  "projectsDataSlice/loadProjectDictionaryData",
  async (
    {
      formDataQuery,
      stackId,
      encodedVersionId,
      modificationId,
    }: {
      formDataQuery?: PageContextOfProjectDataFilter;
      stackId?: number;
      encodedVersionId: string;
      modificationId?: number;
    },
    { dispatch, getState, signal, rejectWithValue },
  ) => {
    const state = getState() as IState;
    const { includePageContext: prevQuery, currentStackId, currentVersionId, currentModificationId } = state.datasetsData;
    const { currentProject } = state.project;
    const { id: projectId } = currentProject ?? {};
    if (signal.aborted) {
      return;
    }
    try {
      if (!projectId) {
        return;
      }
      dispatch(resetFilters(false));
      let actualQuery: PageContextOfProjectDataFilter | undefined;

      if (formDataQuery) {
        dispatch(setPageContext(formDataQuery));
        actualQuery = formDataQuery;
      } else {
        actualQuery = prevQuery;
      }

      const request: GetDataDictionariesPageQuery = {
        projectId: projectId,
        encodedVersionId,
        stackId: stackId,
        context: actualQuery,
        modificationId: modificationId,
      };

      dispatch(projectsDataStart());

      const responseData = await projectsDataClient.pageDataDictionary(request);
      if (signal.aborted) {
        return;
      }

      if (responseData.pageData) {
        if (responseData.totalCount) {
          dispatch(setTotal(responseData.totalCount));
        }
        dispatch(setDictionaryData(responseData.pageData));
      }
      if (
        currentVersionId != encodedVersionId ||
        currentStackId != stackId ||
        currentModificationId != modificationId
      ) {
        dispatch(resetFilters(true));
      }
      dispatch(setCurrentVersionId(encodedVersionId));
      dispatch(setCurrentStackId(stackId ?? null));
      dispatch(setCurrentModificationId(modificationId ?? null));
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const checkLoadingStatus = createAsyncThunk(
  "projectsData/checkLoadingStatus",
  async ({ }, { signal, getState, dispatch }) => {
    const promise = new Promise(() => {
      setTimeout(() => {
        const { processing } = (getState() as IState).projectsData;
        if (processing) {
          if (!signal.aborted) {
            dispatch(setLoadingError(true));
          }
        }
      }, SHOW_ERROR_LOAD_DELAY);
    });
    await promise;
  },
);

export const getTableFilterOptions = createAsyncThunk(
  "projectsData/loadTableFilterOptions",
  async (
    {
      field,
      search,
      stackId,
      modificationId,
    }: { field: VariableDtoKey; search?: string; stackId?: number; modificationId?: number },
    { getState, rejectWithValue },
  ) => {
    const {
      project: { currentProject },
      datasetsData: { includePageContext, currentVersionId },
    } = getState() as IState;
    try {
      const query: GetVariablesFilterOptionsQuery = {
        projectId: currentProject?.id,
        encodedVersionId: currentVersionId ?? "",
        field,
        batteries: includePageContext.filter?.batteries,
        searchText: search,
        stackId,
        modificationId,
      };
      const result = await projectsDataClient.fetchFilterOptions(query);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getCompositeProjectMetadata = createAsyncThunk(
  "projects/getCompositeMetadata",
  async ({ id, version }: { id: number, version: string }, { rejectWithValue }) => {
    try {
      const result = await projectsDataClient.getCompositeMetaData(id, version)
      return result;
    }
    catch (error) {
      return rejectWithValue(error);
    }
  }
)

export const compositeMergeProjects = createAsyncThunk(
  "projects/compositeMregeProjects",
  async ({ projectName, baseProjectId, appendedProjectIds, workdayProjectId }: { projectName: string, baseProjectId: string, appendedProjectIds: Array<string>, workdayProjectId?: number },
    { rejectWithValue, dispatch }) => {
    const compositeMergeCommand: CompositeMergeProjectCommand = {
      appendedProjectIds,
      baseProjectId,
      projectName,
      workdayProjectId
    }
    try {
      const result = await projectsDataClient.compositeMergeProjects(compositeMergeCommand);
      dispatch(addMergeTriggerHistory(result));
      dispatch(
        setLastMergeRequest({
          commandUsed: compositeMergeCommand,
          projectId: result.originProjectId ?? "",  
        })
      );
      dispatch(getCompositeMergeStatus({ token: result.token ?? "", projectId: result.originProjectId ?? "" }));
      return result;
    }
    catch (error) {
      return rejectWithValue(error);
    }
  }
)

export const updateCompositeMetadata = createAsyncThunk(
  "projects/updateCompositeMetadata",
  async ({ id, version, projectName }: { id: number, version: string, projectName: string }, { rejectWithValue, dispatch }) => {
    try {
      const result = await projectsDataClient.updateCompositeMetadata({
        projectId: id,
        projectName,
        version
      });
      dispatch(updateCurrentProjectName({ projectId: id, projectName }));
      dispatch(updateProjectName({ projectId: id, projectName }));
      return result;
    }
    catch (error) {
      return rejectWithValue(error);
    }
  });

export const projectDataTableFilterSubmit =
  (request: TableFilterSubmitProps<string>, encodedVersionId: string, modificationId: number | undefined): AppThunk =>
    (dispatch, getState) => {
      const pageContext = getState()?.projectsData.pageContext;

      const { columnSelector, selectedValues } = request;
      const normalizedSelector = columnSelector as VariableDtoKey;

      const filters = pageContext.filter?.tableFilters?.filter((f) => f.key !== normalizedSelector) ?? [];

      if (selectedValues) {
        filters.push({
          key: columnSelector,
          values: selectedValues,
        });
      }

      const query = {
        ...pageContext,
        filter: {
          ...(pageContext.filter ?? ({} as ProjectDataFilter)),
          tableFilters: filters,
        },
      };
      dispatch(loadProjectDictionaryData({ formDataQuery: query, encodedVersionId, modificationId }));
    };

export const getProjectMatadata = createAsyncThunk(
  "projectData/Metadata",
  async ({
    projectId
  }: {
    projectId: string; 
  }, { rejectWithValue }) => {

    try {
      return  await projectsDataClient.getProjectMetadata(projectId);
    }
    catch (error) {
      rejectWithValue(error);
    }

  });

export default projectsSlice.reducer;
